/**
 * /master/integrations/gmail — Gmail SMTP setup.
 * Lets the master paste a 16-char Google App Password and validate it
 * via /api/integrations/gmail/test (real SMTP login + send to self).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Mail, Loader2, Save, Send, AlertCircle, CheckCircle2, ExternalLink, Trash2, Eye, EyeOff,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MasterGmail() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPwd, setShowPwd] = useState(false);

  // Form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fromName, setFromName] = useState("");
  const [smtpHost, setSmtpHost] = useState("smtp.gmail.com");
  const [smtpPort, setSmtpPort] = useState(587);

  // Action state
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const refresh = () => {
    setLoading(true);
    axios.get(`${API}/integrations/gmail`)
      .then(({ data }) => {
        setStatus(data);
        if (data.configured) {
          setUsername(data.username || "");
          setFromName(data.from_name || "");
          setSmtpHost(data.smtp_host || "smtp.gmail.com");
          setSmtpPort(data.smtp_port || 587);
        }
      })
      .finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const handleSave = async (e) => {
    e.preventDefault();
    setError(""); setSuccess(""); setSaving(true);
    try {
      const { data } = await axios.put(`${API}/integrations/gmail`, {
        username, password, from_name: fromName,
        smtp_host: smtpHost, smtp_port: parseInt(smtpPort, 10),
      });
      setSuccess(`✓ Credenciais salvas para ${data.username}. Agora clique "Testar conexão" para validar.`);
      setPassword(""); // hide pwd from form post-save
      refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setError(""); setSuccess(""); setTesting(true);
    try {
      const { data } = await axios.post(`${API}/integrations/gmail/test`, {});
      setSuccess(`✓ E-mail de teste enviado para ${data.delivered_to} via ${data.host}. Cheque sua caixa de entrada.`);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Remover credenciais Gmail SMTP?")) return;
    await axios.delete(`${API}/integrations/gmail`);
    setUsername(""); setPassword(""); setFromName("");
    setSuccess("Credenciais removidas.");
    refresh();
  };

  return (
    <MasterLayout title="Gmail SMTP — Outreach Real">
      <div data-testid="master-gmail-page" className="max-w-3xl space-y-6">
        {/* Status banner */}
        {!loading && (
          <div className={`rounded-xl p-4 border flex items-start gap-3 ${
            status?.configured
              ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
              : "bg-amber-500/10 border-amber-500/30 text-amber-300"
          }`} data-testid="gmail-status-banner">
            {status?.configured
              ? <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
              : <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />}
            <div className="text-[13px]">
              {status?.configured ? (
                <>
                  Gmail SMTP <strong>configurado</strong> — outbound real ativo de{" "}
                  <code className="text-white">{status.username}</code> via <code className="text-white">{status.smtp_host}:{status.smtp_port}</code>.
                </>
              ) : (
                <>
                  Gmail SMTP <strong>não configurado</strong>. Configure abaixo para o Outreach enviar e-mails reais (substitui o fallback <code>mailto:</code>).
                </>
              )}
            </div>
          </div>
        )}

        {/* Helper card — App Password instructions */}
        <div className="rounded-xl p-5 border"
          style={{ background: "rgba(139,92,246,0.06)", borderColor: "rgba(139,92,246,0.30)" }}>
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-[#A78BFA]" />
            <span className="text-[11px] uppercase tracking-wider font-bold text-[#A78BFA]">
              Como gerar uma App Password
            </span>
          </div>
          <ol className="list-decimal pl-5 space-y-1.5 text-[13px] text-white/80">
            <li>Ative <strong>2-Step Verification</strong> em <a className="underline text-[#A78BFA]" target="_blank" rel="noreferrer" href="https://myaccount.google.com/security">myaccount.google.com/security</a></li>
            <li>Abra <a className="underline text-[#A78BFA]" target="_blank" rel="noreferrer" href="https://myaccount.google.com/apppasswords">myaccount.google.com/apppasswords</a></li>
            <li>Crie uma App Password chamada "AgenteLead Pro SMTP"</li>
            <li>Copie os <strong>16 caracteres</strong> (formato <code className="text-white">abcd efgh ijkl mnop</code>) e cole abaixo</li>
            <li>Clique <strong>Salvar</strong> e depois <strong>Testar conexão</strong></li>
          </ol>
          <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 mt-3 text-[12px] font-bold text-[#A78BFA] hover:text-white"
            data-testid="gmail-apppasswords-link">
            Abrir página de App Passwords <ExternalLink className="w-3 h-3" />
          </a>
        </div>

        {/* Form */}
        <form onSubmit={handleSave} className="rounded-xl p-5 border space-y-4"
          style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(201,162,78,0.20)" }}>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">Gmail (username)</label>
              <input type="email" required value={username} onChange={(e) => setUsername(e.target.value)}
                data-testid="gmail-username"
                placeholder="seu-email@gmail.com"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white placeholder-white/30 focus:border-[#C9A24E] focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">App Password (16 chars)</label>
              <div className="relative">
                <input type={showPwd ? "text" : "password"} required minLength={8} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="gmail-password"
                  placeholder="abcd efgh ijkl mnop"
                  autoComplete="off"
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 pr-10 text-[13.5px] text-white placeholder-white/30 focus:border-[#C9A24E] focus:outline-none font-mono" />
                <button type="button" onClick={() => setShowPwd((v) => !v)}
                  data-testid="gmail-password-toggle"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="text-[10.5px] text-white/45 mt-1.5">
                Espaços são removidos automaticamente. Senha de login normal NÃO funciona.
              </div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">Display Name (opcional)</label>
              <input value={fromName} onChange={(e) => setFromName(e.target.value)}
                data-testid="gmail-from-name"
                placeholder="Marcos · AgenteLead Pro"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white placeholder-white/30 focus:border-[#C9A24E] focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">SMTP Host</label>
                <input value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)}
                  data-testid="gmail-host"
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white focus:border-[#C9A24E] focus:outline-none font-mono" />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-white/55 font-bold mb-2">Port</label>
                <input type="number" value={smtpPort} onChange={(e) => setSmtpPort(e.target.value)}
                  data-testid="gmail-port"
                  className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-[13.5px] text-white focus:border-[#C9A24E] focus:outline-none font-mono" />
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-[13px]"
              data-testid="gmail-error">{error}</div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-[13px]"
              data-testid="gmail-success">{success}</div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <button type="submit" disabled={saving}
              data-testid="gmail-save-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-extrabold uppercase tracking-tight text-[12.5px] disabled:opacity-50 transition-all hover:-translate-y-0.5"
              style={{ background: "#C9A24E", color: "#0F0820", boxShadow: "0 8px 30px -10px rgba(201,162,78,0.55)" }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? "Salvando…" : "Salvar"}
            </button>
            {status?.configured && (
              <>
                <button type="button" onClick={handleTest} disabled={testing}
                  data-testid="gmail-test-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-[12.5px] border disabled:opacity-50 transition-all hover:-translate-y-0.5"
                  style={{ borderColor: "rgba(139,92,246,0.4)", color: "#A78BFA", background: "rgba(139,92,246,0.1)" }}>
                  {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {testing ? "Enviando…" : "Testar conexão"}
                </button>
                <button type="button" onClick={handleDelete}
                  data-testid="gmail-delete-btn"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12.5px] border border-white/15 text-white/55 hover:text-red-300 hover:border-red-500/30 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </MasterLayout>
  );
}
